import {
  ApplicationPermissionsCatalog,
  PermissionCatalogItem,
  PermissionOption,
  PermissionSection,
  RawApplicationCatalog
} from '@shared/types/permissions'

export const OTHER_CATEGORY_KEY = '__other__'

const SCOPE_SEPARATOR = ':'

const legacyLabel = (labels: Record<string, string>, language: string) =>
  labels?.[language] || Object.values(labels ?? {})[0] || ''

export const normalizeApplicationCatalog = (
  application: RawApplicationCatalog,
  language: string
): ApplicationPermissionsCatalog => {
  const instance = application.instance_type
  const catalog = instance?.permissions_catalog

  const permissions: PermissionCatalogItem[] = catalog?.permissions?.length
    ? catalog.permissions.map(({ value, label, description, category }) => ({
        value,
        label: label || value,
        description: description || '',
        category: category ?? null
      }))
    : (instance?.role_permissions ?? []).map(({ value, labels }) => ({
        value,
        label: legacyLabel(labels, language) || value,
        description: '',
        category: null
      }))

  return {
    applicationId: application.id,
    applicationName: application.name || instance?.name || '',
    applicationType: instance?.type ?? '',
    categories: (catalog?.categories ?? []).map(({ key, name }) => ({
      key,
      name
    })),
    permissions
  }
}

type BuildSectionsParams = {
  catalogs: ApplicationPermissionsCatalog[]
  otherLabel: string
  scoped?: boolean
}

export const buildPermissionSections = ({
  catalogs,
  otherLabel,
  scoped = true
}: BuildSectionsParams): PermissionSection[] => {
  const sections: PermissionSection[] = []

  catalogs.forEach((catalog) => {
    if (!catalog.permissions.length) return

    const knownCategories = new Set(catalog.categories.map(({ key }) => key))
    const buckets = new Map<string, PermissionOption[]>()

    catalog.permissions.forEach((item) => {
      const categoryKey =
        item.category && knownCategories.has(item.category)
          ? item.category
          : OTHER_CATEGORY_KEY

      const option: PermissionOption = {
        key: scoped
          ? `${catalog.applicationId}${SCOPE_SEPARATOR}${item.value}`
          : item.value,
        value: item.value,
        label: item.label || item.value,
        description: item.description || undefined
      }

      const bucket = buckets.get(categoryKey)

      if (bucket) bucket.push(option)
      else buckets.set(categoryKey, [option])
    })

    const orderedCategories = [
      ...catalog.categories,
      { key: OTHER_CATEGORY_KEY, name: otherLabel }
    ]

    orderedCategories.forEach(({ key, name }) => {
      const options = buckets.get(key)

      if (!options?.length) return

      sections.push({
        key: `${catalog.applicationId}${SCOPE_SEPARATOR}${key}`,
        title: name,
        groupTitle: catalog.applicationName || undefined,
        options
      })
    })
  })

  return sections
}

export const getPermissionsSummary = (
  sections: PermissionSection[],
  value?: string[] | null
) => {
  const selected = new Set(value ?? [])

  return sections.reduce(
    (summary, { options }) => ({
      active:
        summary.active + options.filter(({ key }) => selected.has(key)).length,
      total: summary.total + options.length
    }),
    { active: 0, total: 0 }
  )
}

export type ScopedPermissions = {
  application: number
  permissions?: Array<string>
}

export const flattenScopedPermissions = (
  list?: ScopedPermissions[] | null
): string[] =>
  (list ?? []).flatMap(({ application, permissions }) =>
    (permissions ?? []).map(
      (value) => `${application}${SCOPE_SEPARATOR}${value}`
    )
  )

export const toScopedPermissions = (
  keys: string[],
  previous?: ScopedPermissions[] | null
): ScopedPermissions[] => {
  const grouped = new Map<number, string[]>()

  ;(previous ?? []).forEach(({ application }) => grouped.set(application, []))

  keys.forEach((key) => {
    const separatorIndex = key.indexOf(SCOPE_SEPARATOR)

    if (separatorIndex === -1) return

    const application = Number(key.slice(0, separatorIndex))
    const value = key.slice(separatorIndex + 1)

    if (!Number.isFinite(application) || !value) return

    const bucket = grouped.get(application)

    if (bucket) bucket.push(value)
    else grouped.set(application, [value])
  })

  return [...grouped.entries()].map(([application, permissions]) => ({
    application,
    permissions
  }))
}
